import React, { useMemo } from 'react';
import mobileGameDefaultIcon from '@/pages/opinion/assets/svg/mobile-game-default.svg';

import starblue from '@op/assets/svg/starblue.svg';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import {
  reviewsFormat,
  scoreFormat,
  yAxleFormat,
} from '@/pages/opinion/utils/value';
import { ratingColors } from '@/pages/opinion/constants';
import {
  echartTooltipTime,
  formatChartParamsDate,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { getIconInfo, getStarElement } from './utils';
import { getPercentageInfo } from '@/pages/opinion/utils';
import { useIntl } from 'umi';
import useGameChartRich, { xArr } from '../../hooks/useGameChartRich';

export default function MobileBarChart({
  height,
  leftTitle,
  rightTitle,
  dataList,
  xAxisList,
  tooltipTitle = '',
  loading = false,
  commonParams = {},
}: BarSingle) {
  const { xRich, gameInfo } = useGameChartRich({
    xAxisList,
    gameInfoKey: 'entity_name',
  });

  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    const option = {
      tooltip: {
        formatter: (params: any) => {
          const timeText = echartTooltipTime(commonParams);
          let [score, ...starInfo] = params || [];
          let { name, value } = score;
          let { cover } = gameInfo[name];

          return formatChartParamsDate({
            title: timeText,
            params: starInfo,
            beforeSort: false,
            textShowFun: (
              marker: string,
              seriesName: string,
              data: any,
              index: number,
            ) => {
              return `<span class="chart-common-formatter-marker">${marker}</span>&nbsp;
                    ${getStarElement(
                      `<img style="width: 16px;height: 16px;vertical-align: middle;" src=${starblue}></img>`,
                      5 - index,
                    )}`;
            },
            valueFormat: (value: number) => reviewsFormat(value),
            dealwithRecords(records, total) {
              return records
                .map((item) => ({
                  ...item,
                  share: getPercentageInfo(item.originValue, total, 2),
                }))
                .reverse();
            },
            tablesConfigFunc: (records: Array<any>) => {
              return [
                {
                  showHeader: true,
                  records: [
                    {
                      text: formatMessage({ id: tooltipTitle }),
                      value: scoreFormat(value),
                    },
                  ],
                  columns: [
                    {
                      key: 'text',
                      header: getIconInfo(cover, name),
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: '',
                    },
                  ],
                },
                {
                  showHeader: true,
                  records,
                  columns: [
                    {
                      key: 'text',
                      header: formatMessage({ id: 'Rating' }),
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Reviews' }),
                    },
                    {
                      key: 'share',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Share-2' }),
                    },
                  ],
                },
              ];
            },
          });
        },
      },
      legend: {
        data: [
          {
            name: 'Score',
            itemStyle: {
              color: '#709fff',
              icon: 'circle',
            },
          },
          {
            name: '1星',
            itemStyle: {
              color: ratingColors[0],
            },
          },
          {
            name: '2星',
            itemStyle: {
              color: ratingColors[1],
            },
          },
          {
            name: '3星',
            itemStyle: {
              color: ratingColors[2],
            },
          },
          {
            name: '4星',
            itemStyle: {
              color: ratingColors[3],
            },
          },
          {
            name: '5星',
            itemStyle: {
              color: ratingColors[4],
            },
          },
        ],
        formatter: (params: any) => {
          if (params !== 'Score') {
            params = parseInt(params);
            let legendIcon = '';
            for (let index = 0; index < params; index++) {
              legendIcon += `{databrainImg|}`;
            }
            return legendIcon;
          } else {
            return `{a|${tooltipTitle}}`;
          }
        },
        textStyle: {
          rich: {
            a: {
              color: '#888888',
              fontSize: 12,
            },
            databrainImg: {
              height: 10,
              width: 10,
              backgroundColor: {
                image: starblue,
              },
            },
          },
        },
      },
      xAxis: {
        data: [],
        axisLabel: {
          interval: 'auto',
          formatter: function (value: any, index: number) {
            if (xArr[index]) {
              return '{' + xArr[index] + '| }';
            }
            return value;
          },
          rich: {
            value: {
              lineHeight: 30,
              align: 'center',
              width: 300,
              height: 300,
            },
            ...xRich,
          },
        },
      },
      yAxis: [
        {
          type: 'value',
          splitLine: {
            show: false,
          },
          axisLabel: {
            formatter: (value: number) => yAxleFormat(value),
            color: '#838697',
          },
          nameTextStyle: {
            color: '#444444',
            fontWeight: 400,
            fontSize: '12px',
          },
          splitNumber: 5,
        },
        {
          type: 'value',
          splitLine: {
            show: false,
          },
          axisLabel: {
            color: '#838697',
          },
          max: 5,
          min: 0,
          splitNumber: 5,
        },
      ],
      series: [
        {
          name: 'Score',
          id: 'Score',
          type: 'line',
          yAxisIndex: 1,
          lineStyle: {
            color: '#5086F3',
          },
        },
        {
          name: '5星',
          id: 'FiveStartCount',
          type: 'bar',
          stack: 'time',
          star: 5,
          barMaxWidth: 50,
          itemStyle: {
            color: ratingColors[4],
          },
        },
        {
          name: '4星',
          id: 'FourStartCount',
          type: 'bar',
          stack: 'time',
          star: 4,
          barMaxWidth: 50,
          itemStyle: {
            color: ratingColors[3],
          },
        },
        {
          name: '3星',
          id: 'ThreeStartCount',
          type: 'bar',
          stack: 'time',
          star: 3,
          barMaxWidth: 50,
          itemStyle: {
            color: ratingColors[2],
          },
        },
        {
          name: '2星',
          id: 'TwoStartCount',
          type: 'bar',
          stack: 'time',
          star: 2,
          barMaxWidth: 50,
          itemStyle: {
            color: ratingColors[1],
          },
        },
        {
          name: '1星',
          id: 'OneStartCount',
          type: 'bar',
          stack: 'time',
          star: 1,
          barMaxWidth: 50,
          itemStyle: {
            color: ratingColors[0],
          },
        },
      ],
      grid: {
        right: 0,
        bottom: 30,
        top: 6,
        containLabel: true,
      },
    };

    if (Array.isArray(dataList) && dataList?.length) {
      let xAxisListData = xAxisList
        ?.map((item: any) => {
          if (item?.entity_type === 'mobile') {
            return item?.entity_name;
          }
        })
        .filter(Boolean);
      option.xAxis.data = xAxisListData;
      option.series = option.series.map((series) => {
        dataList?.forEach((listItem) => {
          if (series.id === listItem?.name) {
            listItem?.games.sort(
              (a, b) =>
                xAxisListData.indexOf(a.name) - xAxisListData.indexOf(b.name),
            );

            series.data = listItem?.games?.map((item) => item.value);
          }
        });
        return series;
      });
    }
    return option;
  }, [dataList, xRich, xAxisList, tooltipTitle]);

  return (
    <EChartCommon
      empty={dataList?.length === 0}
      height={height}
      leftTitle={leftTitle}
      rightTitle={rightTitle}
      options={options}
      loading={loading}
      optionsProps={{ type: 'bar' }}
    ></EChartCommon>
  );
}

type BarSingle = {
  height: number;
  dataList: {
    games: {
      edition_unified_id: string;
      entity_type: string;
      id_type: string;
      rate: number;
      value: number;
      name: string;
    }[];
    name: string;
  }[];
  xAxisList: any;
  tooltipTitle?: string;
  commonParams: object;
  loading: boolean;
  leftTitle?: string;
  rightTitle?: string;
};
